import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerMovimientos, obtenerComprasPeriodo, obtenerProductosVendidos, eliminarVenta } from '../services/balanceService';
import ProductosVendidos from './ProductosVendidos';
import { eliminarGasto } from '../services/gastosService';
import { eliminarIngresoLibre } from '../services/ingresosService';
import { iconoTipo } from '../services/cuentasService';
import { iconoCanal } from '../utils/canales';
import { mensajeAmigable } from '../utils/errores';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { usePlan } from './SoloPlan';
import { segunTienda } from '../utils/tienda';
import EditarVentaModal from './EditarVentaModal';
import EditarGastoModal from './EditarGastoModal';
import EditarIngresoModal from './EditarIngresoModal';
import EditarVentaCreditoModal from './EditarVentaCreditoModal';
import EditarAbonoModal from './EditarAbonoModal';
import { obtenerCxcDeAbono, eliminarAbono, obtenerPorCobrar } from '../services/porCobrarService';
import { descargarCSV, imprimirPDF } from '../utils/exportar';

// Tipos de movimiento que se pueden corregir/eliminar desde el Balance.
const EDITABLES = ['venta', 'gasto', 'ingreso_libre'];

const fmt = (n) => `${simboloMoneda()} ${Number(Math.abs(n) || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODOS = [
  { clave: 'hoy', etiqueta: 'Hoy' },
  { clave: 'semana', etiqueta: 'Semana' },
  { clave: 'mes', etiqueta: 'Mes' },
  { clave: 'anio', etiqueta: 'Año' },
];

const MESES_NOMBRE = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Un mes anterior se guarda como periodo 'm:AAAA-MM'.
const esMesElegido = (p) => typeof p === 'string' && p.startsWith('m:');
const nombreMes = (p) => { const [a, m] = p.slice(2).split('-').map(Number); return `${MESES_NOMBRE[m - 1]} ${a}`; };

// Últimos 24 meses ANTERIORES al actual (el actual ya es el botón "Mes").
function mesesAnteriores() {
  const hoy = new Date();
  const lista = [];
  for (let i = 1; i <= 24; i += 1) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    const clave = `m:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    lista.push({ clave, etiqueta: nombreMes(clave) });
  }
  return lista;
}

// Rango del período en ISO (fecha local): `desde` inclusivo, `hasta` exclusivo
// (null = hasta hoy). Un mes elegido va del día 1 al último día de ese mes.
function rangoDe(periodo) {
  if (esMesElegido(periodo)) {
    const [a, m] = periodo.slice(2).split('-').map(Number);
    return { desde: new Date(a, m - 1, 1).toISOString(), hasta: new Date(a, m, 1).toISOString() };
  }
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (periodo === 'semana') d.setDate(d.getDate() - 6);
  else if (periodo === 'mes') d.setDate(1);
  else if (periodo === 'anio') { d.setMonth(0); d.setDate(1); }
  return { desde: d.toISOString(), hasta: null };
}

const ICONO_MOV = {
  venta: '🛒', ingreso_libre: '💵', gasto: '💸', abono: '💰',
  transferencia_entrada: '🔄', transferencia_salida: '🔄', ajuste: '⚙️', saldo_inicial: '🏦',
};

const TIPOS_INGRESO = ['venta', 'ingreso_libre', 'abono'];
const TIPOS_EGRESO = ['gasto'];

const th = { padding: '10px 12px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '11px 12px', verticalAlign: 'middle' };

const btnBase = { padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const btnExcel = { ...btnBase, backgroundColor: 'var(--k-verde)', color: 'white', border: 'none' };
const btnPdf = { ...btnBase, backgroundColor: 'white', color: '#BE185D', border: '1px solid #F9A8D4' };

export default function BalanceView({ onVolver, onCambio, productos = [] }) {
  const { mostrarToast } = useToast();
  // Corregir o borrar movimientos: la dueña, o quien tenga "Borrar registros".
  const { puede } = useAuth();
  const corregir = puede('borrar');
  // Descargar en Excel/PDF es del plan Pro.
  const plan = usePlan();
  const puedeExportar = plan.tiene('exportar');
  const conPlan = (fn) => (puedeExportar ? fn : () => mostrarToast(segunTienda(`Descargar en Excel o PDF está disponible en el plan ${plan.planNecesario('exportar')}. Míralo en "Mi plan".`, 'Descargar en Excel o PDF no está incluido en tu plan actual.'), 'error', 6000));
  const candado = puedeExportar ? '' : ' 🔒';
  const [periodo, setPeriodo] = useState('mes');
  const [editarRef, setEditarRef] = useState(null);
  const [editarGastoRef, setEditarGastoRef] = useState(null);
  const [editarIngresoRef, setEditarIngresoRef] = useState(null);
  const [editarCreditoCxc, setEditarCreditoCxc] = useState(null);
  const [editarAbonoRef, setEditarAbonoRef] = useState(null);
  const [movs, setMovs] = useState(null);
  const [compras, setCompras] = useState([]);
  const [vendidos, setVendidos] = useState(null); // renglones de productos vendidos del período
  const [ticketsPendientes, setTicketsPendientes] = useState(() => new Set()); // ventas a crédito sin pagar (no reciben boleto)
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('ingresos');
  const [eliminando, setEliminando] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // referencia a confirmar

  const cargar = () => {
    setMovs(null); setError(null);
    const { desde, hasta } = rangoDe(periodo);
    obtenerMovimientos({ desde, hasta })
      .then(setMovs)
      .catch((e) => setError(mensajeAmigable(e)));
    // Compras reales del período (incluye fiadas) para las listas del sorteo.
    obtenerComprasPeriodo({ desde, hasta })
      .then(setCompras)
      .catch(() => setCompras([]));
    setVendidos(null);
    obtenerProductosVendidos({ desde, hasta })
      .then(setVendidos)
      .catch(() => setVendidos([]));
    // Ventas a crédito aún PENDIENTES de pago: no reciben boleto del sorteo.
    obtenerPorCobrar({ soloPendientes: true })
      .then((cxc) => setTicketsPendientes(new Set((cxc || []).map((c) => String(c.ticket_id)))))
      .catch(() => setTicketsPendientes(new Set()));
  };
  useEffect(cargar, [periodo]);

  const resumen = useMemo(() => {
    if (!movs) return null;
    const suma = (pred) => movs.filter(pred).reduce((s, m) => s + Number(m.monto || 0), 0);
    const ventas = suma((m) => m.tipo === 'venta');
    const otros = suma((m) => m.tipo === 'ingreso_libre');
    const abonos = suma((m) => m.tipo === 'abono'); // pagos de ventas a crédito
    const gastos = -suma((m) => m.tipo === 'gasto'); // monto es negativo
    const ingresos = ventas + otros + abonos;
    return { ventas, otros, abonos, ingresos, gastos, balance: ingresos - gastos };
  }, [movs]);

  const ingresos = (movs || []).filter((m) => TIPOS_INGRESO.includes(m.tipo));
  const egresos = (movs || []).filter((m) => TIPOS_EGRESO.includes(m.tipo));
  const lista = tab === 'ingresos' ? ingresos : egresos;

  // Desglose de los movimientos de ingreso por tipo (para cuadrar el total).
  const esEnvioMov = (m) => m.tipo === 'ingreso_libre' && /env[ií]o/i.test(m.descripcion || '');
  const desgloseIngresos = {
    ventasPagadas: ingresos.filter((m) => m.tipo === 'venta').length,
    envios: ingresos.filter(esEnvioMov).length,
    otros: ingresos.filter((m) => m.tipo === 'ingreso_libre' && !esEnvioMov(m)).length,
    abonos: ingresos.filter((m) => m.tipo === 'abono').length,
  };

  const fechaHora = (iso) => new Date(iso).toLocaleString('es-HN', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const soloFecha = (iso) => new Date(iso).toLocaleDateString('es-HN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });

  // Lista limpia de COMPRAS del período (una fila por ticket), sin envíos.
  // Fuente principal: tabla `ventas` (incluye pagadas Y fiadas). Se agregan las
  // "ventas libres" con clienta que viven en los movimientos (ingreso_libre),
  // que no están en la tabla ventas. Los envíos nunca entran.
  const esEnvio = (d) => /env[ií]o/i.test(d || '');
  const todasCompras = useMemo(() => {
    const deVentas = (compras || []).map((c) => ({
      fecha: c.fecha,
      cliente: (c.cliente || '').trim(),
      etiqueta: c.concepto || (c.nProductos === 1 ? (c.primerProducto || 'Venta') : `Venta (${c.nProductos} productos)`),
      total: Math.abs(Number(c.total || 0)),
      canal: c.canal || null,
      medio: c.aCredito ? 'Fiado (crédito)' : (c.metodo || null),
      // Fiada aún sin pagar (su ticket sigue en cuentas por cobrar): no da boleto.
      creditoPendiente: !!c.aCredito && ticketsPendientes.has(String(c.ticket)),
    }));
    const libres = (movs || [])
      .filter((m) => m.tipo === 'ingreso_libre' && !esEnvio(m.descripcion))
      .map((m) => ({
        fecha: m.creado_en,
        cliente: (m.cliente || '').trim(),
        etiqueta: m.descripcion || 'Venta libre',
        total: Math.abs(Number(m.monto || 0)),
        canal: m.canal || null,
        medio: m.cuentas ? m.cuentas.nombre : null,
        creditoPendiente: false,
      }));
    return [...deVentas, ...libres].sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [compras, movs, ticketsPendientes]);

  // Clientas únicas (sin repetir): cuántas compras hizo y cuánto gastó.
  const clientas = useMemo(() => {
    const mapa = new Map();
    todasCompras.forEach((c) => {
      if (!c.cliente) return;
      const cur = mapa.get(c.cliente) || { nombre: c.cliente, compras: 0, total: 0 };
      cur.compras += 1;
      cur.total += c.total;
      mapa.set(c.cliente, cur);
    });
    return [...mapa.values()].sort((a, b) => b.total - a.total);
  }, [todasCompras]);

  // Boletos del sorteo: una fila por CADA compra con clienta (repite el nombre).
  // Se EXCLUYEN las ventas al crédito que siguen pendientes de pago (no dan boleto
  // hasta que se paguen).
  const boletos = useMemo(() => todasCompras.filter((c) => c.cliente && !c.creditoPendiente), [todasCompras]);
  const comprasSinClienta = todasCompras.filter((c) => !c.cliente).length;
  const comprasCreditoPendiente = todasCompras.filter((c) => c.cliente && c.creditoPendiente).length;

  const etiquetaPeriodo = esMesElegido(periodo) ? nombreMes(periodo) : (PERIODOS.find((p) => p.clave === periodo)?.etiqueta || '');
  const etiquetaArchivo = etiquetaPeriodo.replace(/\s+/g, '-');
  const sello = new Date().toLocaleDateString('es-HN').replace(/\//g, '-');
  const subtitulo = `Período: ${etiquetaPeriodo} · Generado el ${new Date().toLocaleDateString('es-HN')}`;

  // ── Exportar: clientas que compraron (sin repetir) ──
  const filasClientas = () => clientas.map((c, i) => [i + 1, c.nombre, c.compras, c.total.toFixed(2)]);
  const encClientas = ['#', 'Clienta', 'Compras', 'Total comprado (L)'];
  const pieClientas = `${clientas.length} clienta(s)` +
    (comprasSinClienta ? ` · ${comprasSinClienta} compra(s) sin clienta registrada (no entran al sorteo)` : '');
  const exportarClientasExcel = () => {
    if (!clientas.length) return mostrarToast('No hay clientas con nombre en este período', 'error');
    descargarCSV(`clientas-que-compraron-${etiquetaArchivo}-${sello}`, encClientas, filasClientas());
  };
  const exportarClientasPDF = () => {
    if (!clientas.length) return mostrarToast('No hay clientas con nombre en este período', 'error');
    imprimirPDF({ titulo: '🎁 Clientas que compraron', subtitulo, encabezados: encClientas, filas: filasClientas(), pie: pieClientas });
  };

  // ── Exportar: ventas del período sin envíos ──
  const filasVentas = () => todasCompras.map((c, i) => [
    i + 1, soloFecha(c.fecha), c.cliente || '—', c.etiqueta,
    c.canal || '—', c.medio || '—', c.total.toFixed(2),
  ]);
  const encVentas = ['#', 'Fecha', 'Clienta', 'Concepto', 'Canal', 'Medio de pago', 'Valor (L)'];
  const exportarVentasExcel = () => {
    if (!todasCompras.length) return mostrarToast('No hay ventas en este período', 'error');
    descargarCSV(`ventas-sin-envios-${etiquetaArchivo}-${sello}`, encVentas, filasVentas());
  };
  const exportarVentasPDF = () => {
    if (!todasCompras.length) return mostrarToast('No hay ventas en este período', 'error');
    imprimirPDF({ titulo: '🛍️ Ventas del período (sin envíos)', subtitulo, encabezados: encVentas, filas: filasVentas(), pie: `${todasCompras.length} venta(s)` });
  };

  // ── Exportar: boletos del sorteo (1 por compra, clientas repetidas) ──
  const filasBoletos = () => boletos.map((c, i) => [i + 1, c.cliente, soloFecha(c.fecha), c.etiqueta]);
  const encBoletos = ['# Boleto', 'Clienta', 'Fecha', 'Compra'];
  const pieBoletos = `${boletos.length} boleto(s) · ${clientas.length} clienta(s) distintas` +
    (comprasCreditoPendiente ? ` · ${comprasCreditoPendiente} venta(s) al crédito pendiente(s) de pago no reciben boleto` : '') +
    (comprasSinClienta ? ` · ${comprasSinClienta} compra(s) sin clienta no reciben boleto` : '');
  const exportarBoletosExcel = () => {
    if (!boletos.length) return mostrarToast('No hay compras con clienta en este período', 'error');
    descargarCSV(`boletos-sorteo-${etiquetaArchivo}-${sello}`, encBoletos, filasBoletos());
  };
  const exportarBoletosPDF = () => {
    if (!boletos.length) return mostrarToast('No hay compras con clienta en este período', 'error');
    imprimirPDF({ titulo: '🎟️ Boletos del sorteo (1 por compra)', subtitulo, encabezados: encBoletos, filas: filasBoletos(), pie: pieBoletos });
  };

  // Abre el editor correcto según el tipo de movimiento.
  const abrirEditar = (m) => {
    if (m.tipo === 'venta') setEditarRef(m.referencia);
    else if (m.tipo === 'gasto') setEditarGastoRef(m.referencia);
    else if (m.tipo === 'ingreso_libre') setEditarIngresoRef(m.referencia);
    else if (m.tipo === 'abono') setEditarAbonoRef(m.referencia);
  };
  // Desde el editor del pago se puede saltar a editar la venta a crédito completa.
  const abrirCreditoDeAbono = async (abonoId) => {
    try {
      const cxc = await obtenerCxcDeAbono(abonoId);
      setEditarAbonoRef(null);
      setEditarCreditoCxc(cxc);
    } catch (e) { mostrarToast(mensajeAmigable(e), 'error'); }
  };
  // Desde "Productos vendidos": una venta fiada abre su editor de crédito;
  // una pagada, el editor de venta normal.
  const abrirVendido = (r) => {
    if (r.a_credito && r.cxc) setEditarCreditoCxc(r.cxc);
    else setEditarRef(r.referencia);
  };
  const puedeEditar = (m) => corregir && (EDITABLES.includes(m.tipo) || m.tipo === 'abono');

  const eliminarMov = (m) => {
    if (m.tipo === 'venta') return eliminarVenta(m.referencia);
    if (m.tipo === 'gasto') return eliminarGasto(m.referencia);
    if (m.tipo === 'ingreso_libre') return eliminarIngresoLibre(m.referencia);
    if (m.tipo === 'abono') return eliminarAbono(m.referencia);
    return Promise.reject(new Error('Este movimiento no se puede eliminar desde aquí'));
  };

  const borrar = async (m) => {
    setEliminando(m.referencia);
    try {
      await eliminarMov(m);
      mostrarToast('Movimiento eliminado. Stock y dinero ajustados ✅', 'exito');
      setConfirmar(null);
      onCambio && onCambio();
      cargar();
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setEliminando(null);
    }
  };

  const Tarjeta = ({ titulo, valor, detalle, color }) => (
    <div className="k-card" style={{ padding: '16px' }}>
      <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{titulo}</p>
      <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color }}>{valor}</p>
      {detalle && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>{detalle}</p>}
    </div>
  );

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">💰 Balance y movimientos</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      {/* Filtros de período */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {PERIODOS.map((p) => (
          <button key={p.clave} onClick={() => setPeriodo(p.clave)}
            style={{ padding: '7px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${periodo === p.clave ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
              backgroundColor: periodo === p.clave ? 'var(--color-primario,#DB2777)' : 'white',
              color: periodo === p.clave ? 'white' : '#6B7280' }}>
            {p.etiqueta}
          </button>
        ))}
        {/* Meses anteriores (ej. ver septiembre completo estando en octubre) */}
        <select value={esMesElegido(periodo) ? periodo : ''} onChange={(e) => { if (e.target.value) setPeriodo(e.target.value); }}
          aria-label="Ver un mes anterior"
          style={{ padding: '7px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${esMesElegido(periodo) ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
            backgroundColor: esMesElegido(periodo) ? 'var(--color-primario,#DB2777)' : 'white',
            color: esMesElegido(periodo) ? 'white' : '#6B7280' }}>
          <option value="">📅 Otro mes…</option>
          {mesesAnteriores().map((m) => <option key={m.clave} value={m.clave}>{m.etiqueta}</option>)}
        </select>
      </div>

      {!movs && !error && <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>⏳ Cargando movimientos...</div>}
      {error && <div style={{ padding: '16px', backgroundColor: '#FEE2E2', borderRadius: '12px', border: '1px solid #FCA5A5', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</div>}

      {resumen && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            <Tarjeta titulo="Balance del período" color={resumen.balance >= 0 ? 'var(--k-verde)' : '#DC2626'}
              valor={`${resumen.balance < 0 ? '-' : ''}${fmt(resumen.balance)}`} detalle="Ingresos − Gastos" />
            <Tarjeta titulo="Ingresos" color="var(--k-verde)" valor={fmt(resumen.ingresos)}
              detalle={(() => {
                const partes = [`Ventas ${fmt(resumen.ventas)}`];
                if (resumen.otros > 0) partes.push(`otros ${fmt(resumen.otros)}`);
                if (resumen.abonos > 0) partes.push(`abonos ${fmt(resumen.abonos)}`);
                return partes.join(' + ');
              })()} />
            <Tarjeta titulo="Gastos" color="#DC2626" valor={fmt(resumen.gastos)} detalle="Salidas del período" />
          </div>

          {(resumen.abonos > 0 || resumen.otros > 0) && (
            <p style={{ margin: '-8px 0 20px', fontSize: '12px', color: '#9CA3AF', lineHeight: 1.5 }}>
              💡 <strong>Abonos</strong> = pagos de ventas <strong>fiadas</strong> (a crédito). <strong>Otros</strong> = envíos cobrados, ventas libres y consultorías. La utilidad por producto se ve en 📊 Reportes.
            </p>
          )}

          {/* Descargar listas (Excel / PDF) */}
          <div style={{ backgroundColor: '#FFF5F7', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#9D174D', fontWeight: 700 }}>
              ⬇️ Descargar lista de <strong>{etiquetaPeriodo}</strong> (sin envíos)
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🎟️ Boletos del sorteo <span style={{ color: '#9CA3AF' }}>(1 por compra · {boletos.length})</span></p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={conPlan(exportarBoletosExcel)} style={btnExcel}>📊 Excel{candado}</button>
                  <button onClick={conPlan(exportarBoletosPDF)} style={btnPdf}>📄 PDF{candado}</button>
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🎁 Clientas (sin repetir) <span style={{ color: '#9CA3AF' }}>({clientas.length})</span></p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={conPlan(exportarClientasExcel)} style={btnExcel}>📊 Excel{candado}</button>
                  <button onClick={conPlan(exportarClientasPDF)} style={btnPdf}>📄 PDF{candado}</button>
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 6px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>🛍️ Ventas detalladas <span style={{ color: '#9CA3AF' }}>({todasCompras.length})</span></p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={conPlan(exportarVentasExcel)} style={btnExcel}>📊 Excel{candado}</button>
                  <button onClick={conPlan(exportarVentasPDF)} style={btnPdf}>📄 PDF{candado}</button>
                </div>
              </div>
            </div>
            {comprasSinClienta > 0 && (
              <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                💡 {comprasSinClienta} venta(s) de este período no tienen clienta registrada, por eso no aparecen en la lista del sorteo. Puedes agregarles la clienta editando la venta.
              </p>
            )}
            {comprasCreditoPendiente > 0 && (
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9CA3AF' }}>
                🧾 {comprasCreditoPendiente} venta(s) al crédito pendiente(s) de pago NO entran al sorteo hasta que se paguen.
              </p>
            )}
          </div>

          {/* Pestañas Ingresos / Egresos */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', borderBottom: '2px solid #F3F4F6' }}>
            {[['ingresos', `Ingresos (${ingresos.length})`], ['egresos', `Egresos (${egresos.length})`],
              ['vendidos', `🛍️ Productos vendidos${vendidos ? ` (${vendidos.length})` : ''}`]].map(([clave, txt]) => (
              <button key={clave} onClick={() => setTab(clave)}
                style={{ padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '14px',
                  color: tab === clave ? 'var(--color-primario,#DB2777)' : '#9CA3AF',
                  borderBottom: `3px solid ${tab === clave ? 'var(--color-primario,#DB2777)' : 'transparent'}`, marginBottom: '-2px' }}>
                {txt}
              </button>
            ))}
          </div>

          {tab === 'ingresos' && ingresos.length > 0 && (
            <p style={{ margin: '-4px 0 12px', fontSize: '12px', color: '#6B7280', lineHeight: 1.6 }}>
              Estos <strong>{ingresos.length}</strong> son movimientos de <strong>dinero</strong>:{' '}
              🛒 {desgloseIngresos.ventasPagadas} ventas pagadas · 📦 {desgloseIngresos.envios} envíos · 💵 {desgloseIngresos.otros} otros ingresos · 💰 {desgloseIngresos.abonos} abonos.
              <br />
              💡 No es lo mismo que las <strong>{todasCompras.length} ventas</strong> de la descarga: ahí van las compras reales (pagadas <strong>y fiadas</strong>, sin envíos ni abonos). Las <strong>fiadas</strong> aparecen en la descarga pero aún no como dinero aquí; los <strong>envíos y abonos</strong> aparecen como dinero aquí pero no son ventas de producto.
            </p>
          )}

          {tab === 'vendidos' ? (
            vendidos === null
              ? <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>⏳ Cargando productos vendidos…</div>
              : <ProductosVendidos renglones={vendidos} productos={productos} onAbrir={abrirVendido} />
          ) : lista.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
              No hay {tab} en este período.
            </div>
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #F3F4F6', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '620px', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB', color: '#6B7280', textAlign: 'left' }}>
                    <th style={th}>Concepto</th>
                    <th style={{ ...th, textAlign: 'right' }}>Valor</th>
                    <th style={th}>Medio de pago</th>
                    {tab === 'ingresos' && <th style={th}>Canal</th>}
                    <th style={th}>Fecha y hora</th>
                    <th style={{ ...th, width: '1%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((m) => (
                    <tr key={m.id} style={{ borderTop: '1px solid #F3F4F6' }}>
                      <td style={td}>
                        <span style={{ marginRight: '8px' }}>{ICONO_MOV[m.tipo] || '•'}</span>
                        {puedeEditar(m) ? (
                          <button onClick={() => abrirEditar(m)} title="Toca para corregir este movimiento"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: 'var(--color-primario,#DB2777)', textDecoration: 'underline', fontSize: '13px', textAlign: 'left' }}>
                            {m.descripcion || m.tipo}
                          </button>
                        ) : (
                          <span style={{ fontWeight: 600, color: '#111827' }}>{m.descripcion || m.tipo}</span>
                        )}
                        {m.cliente && (
                          <div style={{ marginTop: '2px', paddingLeft: '24px', fontSize: '11px', color: '#6B7280', fontWeight: 600 }}>👤 {m.cliente}</div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap', color: Number(m.monto) >= 0 ? 'var(--k-verde)' : '#DC2626' }}>
                        {Number(m.monto) >= 0 ? '+' : '−'}{fmt(m.monto)}
                      </td>
                      <td style={{ ...td, color: '#374151', whiteSpace: 'nowrap' }}>
                        {m.cuentas ? `${iconoTipo(m.cuentas.tipo)} ${m.cuentas.nombre}` : '—'}
                      </td>
                      {tab === 'ingresos' && (
                        <td style={{ ...td, color: '#374151', whiteSpace: 'nowrap' }}>
                          {m.canal ? `${iconoCanal(m.canal)} ${m.canal}` : '—'}
                        </td>
                      )}
                      <td style={{ ...td, color: '#6B7280', whiteSpace: 'nowrap' }}>{fechaHora(m.creado_en)}</td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {corregir && m.tipo === 'abono' && (
                          confirmar === m.referencia ? (
                            <span style={{ display: 'inline-flex', gap: '4px' }}>
                              <button onClick={() => borrar(m)} disabled={eliminando === m.referencia}
                                style={{ padding: '5px 8px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                                {eliminando === m.referencia ? '...' : 'Sí, borrar'}
                              </button>
                              <button onClick={() => setConfirmar(null)} disabled={eliminando === m.referencia}
                                style={{ padding: '5px 8px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>No</button>
                            </span>
                          ) : (
                            <span style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                              <button onClick={() => abrirEditar(m)} title="Corregir este abono"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px' }}>✏️</button>
                              <button onClick={() => setConfirmar(m.referencia)} title="Eliminar este pago (devuelve el dinero)"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '15px' }}>🗑️</button>
                            </span>
                          )
                        )}
                        {corregir && EDITABLES.includes(m.tipo) && (
                          confirmar === m.referencia ? (
                            <span style={{ display: 'inline-flex', gap: '4px' }}>
                              <button onClick={() => borrar(m)} disabled={eliminando === m.referencia}
                                style={{ padding: '5px 8px', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                                {eliminando === m.referencia ? '...' : 'Sí, borrar'}
                              </button>
                              <button onClick={() => setConfirmar(null)} disabled={eliminando === m.referencia}
                                style={{ padding: '5px 8px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                                No
                              </button>
                            </span>
                          ) : (
                            <span style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                              <button onClick={() => abrirEditar(m)} title="Corregir este movimiento"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px' }}>✏️</button>
                              <button onClick={() => setConfirmar(m.referencia)} title="Eliminar (devuelve stock y dinero)"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '15px' }}>🗑️</button>
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {corregir && tab !== 'vendidos' && (
          <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
              ✏️ Toca una venta, gasto o ingreso (o el lápiz) para corregirlo · 🗑️ para eliminarlo. En todos los casos el stock y el dinero se ajustan solos. Las transferencias entre cuentas se ven en 🏦 Cuentas.
            </p>
          )}
        </>
      )}

      {editarRef && (
        <EditarVentaModal
          referencia={editarRef}
          productos={productos}
          onCerrar={() => setEditarRef(null)}
          onGuardado={() => { onCambio && onCambio(); cargar(); }}
        />
      )}

      {editarGastoRef && (
        <EditarGastoModal
          referencia={editarGastoRef}
          onCerrar={() => setEditarGastoRef(null)}
          onGuardado={() => { onCambio && onCambio(); cargar(); }}
        />
      )}

      {editarIngresoRef && (
        <EditarIngresoModal
          referencia={editarIngresoRef}
          onCerrar={() => setEditarIngresoRef(null)}
          onGuardado={() => { onCambio && onCambio(); cargar(); }}
        />
      )}

      {editarAbonoRef && (
        <EditarAbonoModal
          referencia={editarAbonoRef}
          onEditarVenta={() => abrirCreditoDeAbono(editarAbonoRef)}
          onCerrar={() => setEditarAbonoRef(null)}
          onGuardado={() => { onCambio && onCambio(); cargar(); }}
        />
      )}

      {editarCreditoCxc && (
        <EditarVentaCreditoModal
          cxc={editarCreditoCxc}
          productos={productos}
          onCerrar={() => setEditarCreditoCxc(null)}
          onGuardado={() => { onCambio && onCambio(); cargar(); }}
        />
      )}
    </div>
  );
}
